import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { 
  BarChart3, 
  FileText, 
  TrendingUp, 
  TrendingDown, 
  Link as LinkIcon, 
  FileSearch, 
  Sliders,
  Calendar,
  Code,
  BookOpen,
  Settings,
  Tag,
  Layers,
  Coins,
  Users,
  Grid3X3,
  Package
} from "lucide-react";
import { 
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuContent
} from "@/components/ui/navigation-menu";
import { useCustomPlanNavigation, FEATURE_NAV_CONFIG } from "@/hooks/useCustomPlanNavigation";
import { Skeleton } from "@/components/ui/skeleton";

// Icon mapping for feature keys
const iconMap: Record<string, React.ElementType> = {
  BarChart3,
  FileText,
  TrendingUp,
  TrendingDown,
  Link: LinkIcon,
  FileSearch,
  Sliders,
  Calendar,
  Code,
  Tag,
  Layers,
  Coins,
  Users,
  Grid3X3,
  Package,
};

export function CustomPlanNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isLoading, isCustomPlan, navigationConfig, featureKeys } = useCustomPlanNavigation();

  const handleInternalNavigation = (event: React.MouseEvent<HTMLElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    const target = event.target as HTMLElement;
    const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
    const href = anchor?.getAttribute("href");

    if (!href || !href.startsWith("/")) return;

    event.preventDefault();
    navigate(href);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Skeleton className="h-10 w-24 rounded-full" />
        <Skeleton className="h-10 w-24 rounded-full" />
      </div>
    );
  }

  // If not custom plan, don't render (parent should use regular navigation)
  if (!isCustomPlan || !navigationConfig) {
    return null;
  }

  const isActive = (path: string) => location.pathname.startsWith(path);
  const hasFeature = (key: string) => featureKeys.includes(key);

  // Helper to get icon component
  const getIcon = (iconName: string) => {
    const Icon = iconMap[iconName] || BarChart3;
    return <Icon className="h-4 w-4" />;
  };

  // Check which main features are enabled
  const hasContractManagement = hasFeature('contract_management');
  const hasSalesAnalytics = hasFeature('sales_analytics');
  const hasCostOptimization = hasFeature('cost_optimization');
  const hasErpIntegration = hasFeature('erp_integration');
  const hasDocumentExtraction = hasFeature('document_extraction');
  const hasCustomFields = hasFeature('custom_fields');
  const hasDashboard = hasFeature('dashboard') || hasFeature('advanced_dashboard');
  const hasCalendar = hasFeature('calendar_view');
  const hasApiAccess = hasFeature('api_read') || hasFeature('api_write');

  return (
    <NavigationMenu onClickCapture={handleInternalNavigation}>
      <NavigationMenuList className="flex items-center space-x-1">
        {/* Dashboard - if enabled */}
        {hasDashboard && (
          <NavigationMenuItem>
            <Link to="/dashboard">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/dashboard")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <BarChart3 className="h-4 w-4" />
                <span>{t('nav.dashboard', 'Dashboard')}</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* Contracts - if contract_management enabled */}
        {hasContractManagement && (
          <NavigationMenuItem>
            <NavigationMenuTrigger 
              className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/contracts") || isActive("/categories") || isActive("/services")
                  ? "bg-secondary text-secondary-foreground shadow-md" 
                  : "hover:bg-muted text-foreground"
              )}
            >
              <FileText className="h-4 w-4" />
              {t('nav.contracts', 'Contracts')}
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <div className="p-4 w-[400px]">
                <div className="grid gap-2">
                  <NavigationMenuLink asChild>
                    <a href="/contracts" className="block p-3 rounded-xl hover:bg-muted transition-colors">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-primary" />
                        <div>
                          <div className="font-medium text-sm">{t('nav.allContracts', 'All Contracts')}</div>
                          <p className="text-xs text-muted-foreground">{t('nav.allContractsDesc', 'Browse and manage contracts')}</p>
                        </div>
                      </div>
                    </a>
                  </NavigationMenuLink>
                  <NavigationMenuLink asChild>
                    <a href="/contracts/new" className="block p-3 rounded-xl hover:bg-muted transition-colors">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-success" />
                        <div>
                          <div className="font-medium text-sm">{t('nav.newContract', 'New Contract')}</div>
                          <p className="text-xs text-muted-foreground">{t('nav.newContractDesc', 'Create a new contract')}</p>
                        </div>
                      </div>
                    </a>
                  </NavigationMenuLink>
                  <NavigationMenuLink asChild>
                    <a href="/categories" className="block p-3 rounded-xl hover:bg-muted transition-colors">
                      <div className="flex items-center gap-3">
                        <Tag className="h-5 w-5 text-warning" />
                        <div>
                          <div className="font-medium text-sm">{t('nav.categories', 'Categories')}</div>
                          <p className="text-xs text-muted-foreground">{t('nav.categoriesDesc', 'Organize contracts')}</p>
                        </div>
                      </div>
                    </a>
                  </NavigationMenuLink>
                  <NavigationMenuLink asChild>
                    <a href="/services" className="block p-3 rounded-xl hover:bg-muted transition-colors">
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-purple-500" />
                        <div>
                          <div className="font-medium text-sm">{t('nav.services', 'Services')}</div>
                          <p className="text-xs text-muted-foreground">{t('nav.servicesDesc', 'Manage contract services')}</p>
                        </div>
                      </div>
                    </a>
                  </NavigationMenuLink>
                </div>
              </div>
            </NavigationMenuContent>
          </NavigationMenuItem>
        )}

        {/* Sales Analytics - if enabled */}
        {hasSalesAnalytics && (
          <NavigationMenuItem>
            <Link to="/sales-analytics">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/sales-analytics")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <TrendingUp className="h-4 w-4" />
                <span>{t('nav.salesAnalytics', 'Sales Analytics')}</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* Cost Optimization - if enabled */}
        {hasCostOptimization && (
          <NavigationMenuItem>
            <Link to="/cost-optimization">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/cost-optimization")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <TrendingDown className="h-4 w-4" />
                <span>{t('nav.costOptimization', 'Cost Optimization')}</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* ERP Integration - if enabled */}
        {hasErpIntegration && (
          <NavigationMenuItem>
            <Link to="/settings/integrations">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/settings/integrations") || isActive("/erp-integration")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <LinkIcon className="h-4 w-4" />
                <span>{t('nav.erpIntegration', 'ERP Integration')}</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* Document Extraction - if enabled */}
        {hasDocumentExtraction && (
          <NavigationMenuItem>
            <Link to="/documents/extraction">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/documents/extraction") || isActive("/contracts/extract")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <FileSearch className="h-4 w-4" />
                <span>{t('nav.documentExtraction', 'Document Extraction')}</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* Custom Fields - if enabled */}
        {hasCustomFields && !hasContractManagement && (
          <NavigationMenuItem>
            <Link to="/settings/custom-fields">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/settings/custom-fields")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <Sliders className="h-4 w-4" />
                <span>{t('nav.customFields', 'Custom Fields')}</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* Calendar - if enabled */}
        {hasCalendar && (
          <NavigationMenuItem>
            <Link to="/calendar">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/calendar")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <Calendar className="h-4 w-4" />
                <span>{t('nav.calendarView', 'Calendar')}</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* API Portal - if API access enabled */}
        {hasApiAccess && (
          <NavigationMenuItem>
            <Link to="/api-portal">
              <div className={cn(
                "group px-5 py-2 rounded-full font-medium transition-all duration-200",
                "flex items-center gap-2",
                isActive("/api-portal")
                  ? "bg-secondary text-secondary-foreground shadow-md"
                  : "hover:bg-muted text-foreground"
              )}>
                <Code className="h-4 w-4" />
                <span>{t('nav.apiPortal', 'API')}</span>
              </div>
            </Link>
          </NavigationMenuItem>
        )}

        {/* Settings is only accessible via user menu, not shown in top bar */}
        {/* Help - always visible */}
        <NavigationMenuItem>
          <Link to="/documentation">
            <div className={cn(
              "group px-5 py-2 rounded-full font-medium transition-all duration-200",
              "flex items-center gap-2",
              isActive("/documentation")
                ? "bg-secondary text-secondary-foreground shadow-md"
                : "hover:bg-muted text-foreground"
            )}>
              <BookOpen className="h-4 w-4" />
              <span>{t('nav.help', 'Help')}</span>
            </div>
          </Link>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
